import Vue from 'vue';
import qs from 'qs';
import { http } from '@/http';
import Modal, { ModalLinePrimary, ModalLineSecondary } from '@/components/Modal';
import Input from '@/components/Controls/Input';
import Button from '@/components/Controls/Button';
import Checkbox from '@/components/Controls/Checkbox';
import { CmsError } from '@/http/CmsError';
import { Airplane } from './Airplane';
import * as events from '@/metrix/events';
import { FeedbackWidgetData, FormModel, ModalData } from './types';
import './FeedbackWidget.scss';
import { RootElementAttrsHolder } from '@/plugins/RootElementAttrsPlugin';

const defaults: ModalData = {
    action: null,
    linePrimary: 'Обратный звонок',
    lineSecondary: 'Мы свяжемся с Вами в ближайшее время',
    submitButtonText: 'Отправить',
    metrix: {},
    trailer: {},
    whatsappButton: {},
    message: false,
    imageUrlDesktop: null,
    imageUrlMobile: null
};

const getInitialFormModel = (): FormModel => ({
    name: null,
    phone: null,
    message: null,
    agreement: true,
});

export function createFeedbackWidget(el: Element) {
    const rootAttrs = new RootElementAttrsHolder(el);

    return new Vue({
        el,
        name: 'FeedbackWidget',
        rootAttrs,
        data(): FeedbackWidgetData {
            return {
                active: false,
                formModel: getInitialFormModel(),
                fieldErrors: {},
                formErrors: [],
                errorMessage: null,
                modalData: {} as ModalData,
                successfullySent: false,
            };
        },

        methods: {
            show(modalData?: ModalData) {
                this.modalData = Object.assign({}, defaults, modalData || {});
                this.active = true;

                this.$metrix.$emit(
                    events.OPEN,
                    this.getMetrixPayload(this.modalData.metrix[events.OPEN])
                );
            },

            close() {
                this.resetErrors();

                if (this.successfullySent) {
                    this.$metrix.$emit(
                        events.CLOSE,
                        this.getMetrixPayload(this.modalData.metrix?.[events.CLOSE])
                    );
                } else {
                    this.$metrix.$emit(
                        events.CANCEL,
                        this.getMetrixPayload(this.modalData.metrix?.[events.CANCEL])
                    );
                }

                this.active = false;

                if (this.successfullySent) {
                    this.formModel = getInitialFormModel();
                    this.successfullySent = false;
                }
            },

            async submit(e: SubmitEvent) {
                e.preventDefault();

                if (
                    !this.formModel.phone ||
                    this.formModel.phone.replace(/_/g, '').length < 18
                ) {
                    this.$set(this.fieldErrors, 'phone', 'Введите номер телефона');

                    return;
                }

                if (!this.modalData.action) {
                    console.warn(`[${this.$options.name}]: empty action string`);
                    return;
                }

                const dataMap = {
                    ...this.formModel,
                    ...this.modalData.trailer,
                    needValidation: true,
                    validationDate: this.getValidationDate(),
                };

                const data = qs.stringify(dataMap);

                this.$wait.start('formSend');

                try {
                    await http({
                        url: this.modalData.action,
                        method: 'post',
                        headers: { 'content-type': 'application/x-www-form-urlencoded' },
                        data,
                    });

                    const metrixPayload = this.getMetrixPayload(
                        this.modalData.metrix[events.SEND]
                    );

                    this.$metrix.$emit(
                        events.SEND,
                        Object.assign({}, metrixPayload, {
                            formData: dataMap, // TODO выпилить - поля формы добавляются через this.getMetrixPayload
                        })
                    );

                    this.successfullySent = true;
                } catch (error) {
                    this.$errorLog(error);
                    // сбрасываем все поля ошибок, чтобы отображались только актуальные
                    this.resetErrors();

                    if (error instanceof CmsError) {
                        const data = error.details.data || {};
                        const { fieldErrors, formErrors, message } = data;
                        this.fieldErrors = fieldErrors || {};
                        this.formErrors = formErrors || [];
                        this.errorMessage = message || null;
                    } else {
                        this.errorMessage = this.getErrorMessage(error);
                    }
                } finally {
                    this.$wait.end('formSend');
                }
            },

            getErrorMessage(error: any) {
                if (error.response) {
                    const message = error.message ? `(${error.message})` : '';
                    return `Сервер отклонил запрос ${message}`;
                } else if (error.request) {
                    return 'Сервер не отвечает';
                } else {
                    return 'Неизвестная ошибка';
                }
            },

            resetErrors() {
                this.fieldErrors = {};
                this.formErrors = [];
                this.errorMessage = null;
            },

            deleteError(errorKey: string) {
                if (this.fieldErrors[errorKey]) {
                    this.$delete(this.fieldErrors, errorKey);
                }
            },

            getMetrixPayload(input: string) {
                const $form = Object.assign({}, this.formModel, this.modalData.trailer);

                return this.$enrichVars(input || {}, { $form });
            },

            getValidationDate() {
                const date = new Date();
                const year = date.getFullYear();
                const month = date.getMonth() + 1;
                const day = date.getDate();

                return [year, month, day].map((n) => (n < 10 ? `0${n}` : n)).join('-');
            },

            sendWhatsapp(){
                this.$metrix.$emit(events.CLICK,this.modalData.whatsappButton);

                if(window.screen.width > 959) {
                    window.open('https://wa.me/+79770893433');
                }else{
                    location.href = 'https://wa.me/+79770893433';
                }

            }
        },

        render(h) {
            return (
                <Modal
                    active={this.active}
                    onClose={() => this.close()}
                    swipeDownMaxDistance={150}
                    backgrondImgDesktop={this.modalData.imageUrlDesktop}
                >
                    <div
                        class={{
                            'feedback-container': true,
                            'backgrondImgMobile': this.modalData.imageUrlMobile,
                        }}
                        style={{'background-image': (this.modalData.imageUrlMobile) ? 'url('+this.modalData.imageUrlMobile+')' : false }}
                    >
                        <transition name="fade">
                            {this.successfullySent && (
                                <div class="feedback-container__success">
                                    <ModalLinePrimary class="text-center">
                                        Спасибо за обращение!
                                    </ModalLinePrimary>

                                    <div class="feedback-container__airplane">
                                        <div class="feedback-container__airplane-box">
                                            <Airplane />
                                        </div>
                                    </div>

                                    <Button
                                        onClick={() => this.close()}
                                        modifiers={['primary', 'block']}
                                    >
                                        Закрыть
                                    </Button>
                                </div>
                            )}
                        </transition>

                        <div
                            class={{
                                'feedback-container__body': true,
                                'feedback-container__body--sent': this.successfullySent,
                            }}
                        >
                            <ModalLinePrimary class="text-center">
                                <span domPropsInnerHTML={this.modalData.linePrimary} />
                            </ModalLinePrimary>

                            <ModalLineSecondary class="text-center feedback-container__line-secondary">
                                <span domPropsInnerHTML={this.modalData.lineSecondary} />
                            </ModalLineSecondary>

                            <div class="feedback-container__error-pre feedback-error">
                                {this.errorMessage}
                            </div>

                            <form
                                action="/"
                                onSubmit={this.submit}
                                class="grid grid--y-default"
                            >
                                <div class="col">
                                    <Input
                                        placeholder={'Ваше имя'}
                                        vModel={this.formModel.name}
                                        error={this.fieldErrors.name}
                                        onInput={() => {
                                            this.deleteError('name');
                                        }}
                                    />
                                </div>
                                <div class="col">
                                    <Input
                                        type="tel"
                                        mask={{
                                            mask: '+7 (999) 999-99-99[9]',
                                            placeholder: '_',
                                            showMaskOnHover: false,
                                        }}
                                        placeholder={'Номер телефона'}
                                        vModel={this.formModel.phone}
                                        error={this.fieldErrors.phone}
                                        onInput={() => {
                                            this.deleteError('phone');
                                        }}
                                        inputmode="numeric"
                                    />
                                </div>
                                {this.modalData.message && (
                                    <div class="col">
                                        <Input
                                            rows={2}
                                            vModel={this.formModel.message}
                                            placeholder="Сообщение"
                                        />
                                    </div>
                                )}
                                <div class="col text-left">
                                    <Checkbox vModel={this.formModel.agreement}>
                                        согласен с условиями передачи данных
                                    </Checkbox>
                                </div>

                                <div class="feedback-error feedback-container__error-after">
                                    {[].concat(this.formErrors).join(', ')}
                                </div>

                                <div class="col">
                                    <Button
                                        modifiers={['primary', 'block']}
                                        type="submit"
                                        loading={this.$wait.waiting('formSend')}
                                        disabled={!this.formModel.agreement}
                                    >
                                        <span
                                            domPropsInnerHTML={
                                                this.modalData.submitButtonText
                                            }
                                        />
                                    </Button>

                                    { window.screen.width > 959 && this.modalData.whatsappButton && this.modalData.whatsappButton.event &&  (
                                        <div class="whatsapp-block">
                                            <div class="whatsapp-block_text">Вы&nbsp;также&nbsp;можете&nbsp;задать вопрос&nbsp;в&nbsp;WhatsApp</div>
                                            <div class="whatsapp-block_icon">
                                                <svg onClick={() => this.sendWhatsapp()} width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <rect width="50" height="50" rx="10" fill="#2CB742"/>
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M42 24.5592C42 33.7048 34.5272 41.1216 25.3099 41.1216C22.3819 41.1216 19.6313 40.374 17.2421 39.0595L8 41.9969L11.0122 33.113C9.49208 30.6179 8.61677 27.6898 8.61677 24.5623C8.61677 15.4137 16.0896 8 25.3068 8C34.524 8 41.9969 15.4168 41.9969 24.5623L42 24.5592ZM25.3099 10.6353C17.5692 10.6353 11.277 16.8808 11.277 24.5592C11.277 27.6057 12.2675 30.4279 13.9496 32.7236L12.1959 37.8945L17.5879 36.1812C19.8027 37.6359 22.4566 38.4832 25.3099 38.4832C33.0475 38.4832 39.3429 32.2377 39.3429 24.5592C39.3429 16.8808 33.0475 10.6353 25.3099 10.6353ZM33.7391 28.3751C33.6363 28.2069 33.3622 28.1041 32.9541 27.9016C32.546 27.6991 30.5306 26.7148 30.1568 26.5809C29.783 26.4469 29.5089 26.3784 29.2379 26.7833C28.9638 27.1883 28.182 28.1041 27.9421 28.3751C27.7054 28.6461 27.4655 28.6804 27.0574 28.4779C26.6463 28.2754 25.3286 27.8455 23.768 26.4625C22.5532 25.3847 21.7308 24.0577 21.491 23.6497C21.2542 23.2447 21.4661 23.0235 21.6685 22.8211C21.8523 22.6373 22.0797 22.3476 22.2822 22.1109C22.4878 21.8741 22.5563 21.7028 22.6902 21.4318C22.8273 21.1608 22.7588 20.9241 22.656 20.7216C22.5532 20.5191 21.7371 18.5224 21.3944 17.7063C21.0549 16.8964 20.7153 16.9244 20.4755 16.9244C20.2356 16.9244 19.7964 16.9961 19.7964 16.9961C19.7964 16.9961 18.9772 17.0989 18.6003 17.5038C18.2265 17.9088 17.1674 18.8931 17.1674 20.8898C17.1674 22.8865 18.6314 24.8178 18.837 25.0888C19.0426 25.3598 21.6654 29.593 25.827 31.2191C29.9855 32.8451 29.9855 32.3031 30.7362 32.2345C31.4838 32.1691 33.1566 31.2533 33.4992 30.3064C33.8419 29.3594 33.8419 28.5464 33.7391 28.3782V28.3751Z" fill="white"/>
                                                </svg>
                                            </div>
                                        </div>
                                    )}

                                    { window.screen.width < 960 && this.modalData.whatsappButton && this.modalData.whatsappButton.event &&  (
                                        <div class="whatsapp-block">
                                            <div class="whatsapp-block_icon">
                                                <svg onClick={() => this.sendWhatsapp()} width="50" height="50" viewBox="0 0 50 50" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <rect width="50" height="50" rx="10" fill="#2CB742"/>
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M42 24.5592C42 33.7048 34.5272 41.1216 25.3099 41.1216C22.3819 41.1216 19.6313 40.374 17.2421 39.0595L8 41.9969L11.0122 33.113C9.49208 30.6179 8.61677 27.6898 8.61677 24.5623C8.61677 15.4137 16.0896 8 25.3068 8C34.524 8 41.9969 15.4168 41.9969 24.5623L42 24.5592ZM25.3099 10.6353C17.5692 10.6353 11.277 16.8808 11.277 24.5592C11.277 27.6057 12.2675 30.4279 13.9496 32.7236L12.1959 37.8945L17.5879 36.1812C19.8027 37.6359 22.4566 38.4832 25.3099 38.4832C33.0475 38.4832 39.3429 32.2377 39.3429 24.5592C39.3429 16.8808 33.0475 10.6353 25.3099 10.6353ZM33.7391 28.3751C33.6363 28.2069 33.3622 28.1041 32.9541 27.9016C32.546 27.6991 30.5306 26.7148 30.1568 26.5809C29.783 26.4469 29.5089 26.3784 29.2379 26.7833C28.9638 27.1883 28.182 28.1041 27.9421 28.3751C27.7054 28.6461 27.4655 28.6804 27.0574 28.4779C26.6463 28.2754 25.3286 27.8455 23.768 26.4625C22.5532 25.3847 21.7308 24.0577 21.491 23.6497C21.2542 23.2447 21.4661 23.0235 21.6685 22.8211C21.8523 22.6373 22.0797 22.3476 22.2822 22.1109C22.4878 21.8741 22.5563 21.7028 22.6902 21.4318C22.8273 21.1608 22.7588 20.9241 22.656 20.7216C22.5532 20.5191 21.7371 18.5224 21.3944 17.7063C21.0549 16.8964 20.7153 16.9244 20.4755 16.9244C20.2356 16.9244 19.7964 16.9961 19.7964 16.9961C19.7964 16.9961 18.9772 17.0989 18.6003 17.5038C18.2265 17.9088 17.1674 18.8931 17.1674 20.8898C17.1674 22.8865 18.6314 24.8178 18.837 25.0888C19.0426 25.3598 21.6654 29.593 25.827 31.2191C29.9855 32.8451 29.9855 32.3031 30.7362 32.2345C31.4838 32.1691 33.1566 31.2533 33.4992 30.3064C33.8419 29.3594 33.8419 28.5464 33.7391 28.3782V28.3751Z" fill="white"/>
                                                </svg>
                                            </div>
                                            <div class="whatsapp-block_text">Вы&nbsp;также&nbsp;можете&nbsp;задать вопрос&nbsp;в&nbsp;WhatsApp</div>
                                        </div>
                                    )}

                                </div>
                            </form>
                        </div>
                    </div>
                </Modal>
            );
        },
    });
}

// export class FeedbackWidget extends Widget {
//     constructor(selector) {
//         super(selector);

//         const rootProps = {};

//         this.init({
//             name: this.constructor.name,

//             wait,

//             data: {
//                 rootProps,
//                 active: false,
//                 formModel: getInitialFormModel(),
//                 fieldErrors: {},
//                 formErrors: [],
//                 errorMessage: null,
//                 modalData: {},
//                 successfullySent: false,
//             },

//             methods: {
//                 show(modalData = {}) {
//                     this.modalData = Object.assign({}, defaults, modalData);
//                     this.active = true;

//                     this.$metrix.$emit(
//                         events.OPEN,
//                         this.getMetrixPayload(this.modalData.metrix[events.OPEN])
//                     );
//                 },

//                 close() {
//                     this.resetErrors();

//                     if (this.successfullySent) {
//                         this.$metrix.$emit(
//                             events.CLOSE,
//                             this.getMetrixPayload(this.modalData.metrix[events.CLOSE])
//                         );
//                     } else {
//                         this.$metrix.$emit(
//                             events.CANCEL,
//                             this.getMetrixPayload(this.modalData.metrix[events.CANCEL])
//                         );
//                     }

//                     this.active = false;

//                     if (this.successfullySent) {
//                         this.formModel = getInitialFormModel();
//                         this.successfullySent = false;
//                     }
//                 },

//                 async submit(e) {
//                     e.preventDefault();

//                     if (
//                         !this.formModel.phone ||
//                         this.formModel.phone.replace(/_/g, '').length < 18
//                     ) {
//                         this.$set(this.fieldErrors, 'phone', 'Введите номер телефона');

//                         return;
//                     }

//                     if (!this.modalData.action) {
//                         console.warn(`[${this.$options.name}]: empty action string`);
//                         return;
//                     }

//                     const dataMap = {
//                         ...this.formModel,
//                         ...this.modalData.trailer,
//                         needValidation: true,
//                         validationDate: this.getValidationDate(),
//                     };

//                     const data = qs.stringify(dataMap);

//                     this.$wait.start('formSend');

//                     try {
//                         await http({
//                             url: this.modalData.action,
//                             method: this.modalData.method,
//                             data,
//                             headers: {
//                                 'content-type': 'application/x-www-form-urlencoded',
//                             },
//                         });

//                         const metrixPayload = this.getMetrixPayload(
//                             this.modalData.metrix[events.SEND]
//                         );

//                         this.$metrix.$emit(
//                             events.SEND,
//                             Object.assign({}, metrixPayload, {
//                                 formData: dataMap, // TODO выпилить - поля формы добавляются через this.getMetrixPayload
//                             })
//                         );

//                         this.successfullySent = true;
//                     } catch (error) {
//                         this.$errorLog(error);
//                         // сбрасываем все поля ошибок, чтобы отображались только актуальные
//                         this.resetErrors();

//                         if (error instanceof CmsError) {
//                             const data = error.details.data || {};
//                             const { fieldErrors, formErrors, message } = data;
//                             this.fieldErrors = fieldErrors || {};
//                             this.formErrors = formErrors || [];
//                             this.errorMessage = message || null;
//                         } else {
//                             this.errorMessage = this.getErrorMessage(error);
//                         }
//                     } finally {
//                         this.$wait.end('formSend');
//                     }
//                 },

//                 getErrorMessage(error) {
//                     if (error.response) {
//                         const message = error.message ? `(${error.message})` : '';
//                         return `Сервер отклонил запрос ${message}`;
//                     } else if (error.request) {
//                         return 'Сервер не отвечает';
//                     } else {
//                         return 'Неизвестная ошибка';
//                     }
//                 },

//                 resetErrors() {
//                     this.fieldErrors = {};
//                     this.formErrors = [];
//                     this.errorMessage = null;
//                 },

//                 deleteError(errorKey) {
//                     if (this.fieldErrors[errorKey]) {
//                         this.$delete(this.fieldErrors, errorKey);
//                     }
//                 },

//                 getMetrixPayload(input) {
//                     const $form = Object.assign(
//                         {},
//                         this.formModel,
//                         this.modalData.trailer
//                     );

//                     return this.$enrichVars(input || {}, { $form });
//                 },

//                 getValidationDate() {
//                     const date = new Date();
//                     const year = date.getFullYear();
//                     const month = date.getMonth() + 1;
//                     const day = date.getDate();

//                     return [year, month, day]
//                         .map((n) => (n < 10 ? `0${n}` : n))
//                         .join('-');
//                 },
//             },

//             render(h) {
//                 return (
//                     <Modal
//                         active={this.active}
//                         onClose={() => this.close()}
//                         swipeDownMaxDistance={150}
//                     >
//                         <div class="feedback-container">
//                             <Transition name="fade">
//                                 {this.successfullySent && (
//                                     <div class="feedback-container__success">
//                                         <ModalLinePrimary class="text-center">
//                                             Спасибо за обращение!
//                                         </ModalLinePrimary>

//                                         <div class="feedback-container__airplane">
//                                             <div class="feedback-container__airplane-box">
//                                                 <Airplane />
//                                             </div>
//                                         </div>

//                                         <Button
//                                             onClick={() => this.close()}
//                                             modifiers={['primary', 'block']}
//                                         >
//                                             Закрыть
//                                         </Button>
//                                     </div>
//                                 )}
//                             </Transition>

//                             <div
//                                 class={{
//                                     'feedback-container__body': true,
//                                     'feedback-container__body--sent': this
//                                         .successfullySent,
//                                 }}
//                             >
//                                 <ModalLinePrimary class="text-center">
//                                     <span
//                                         domPropsInnerHTML={this.modalData.linePrimary}
//                                     />
//                                 </ModalLinePrimary>

//                                 <ModalLineSecondary class="text-center feedback-container__line-secondary">
//                                     <span
//                                         domPropsInnerHTML={this.modalData.lineSecondary}
//                                     />
//                                 </ModalLineSecondary>

//                                 <div class="feedback-container__error-pre feedback-error">
//                                     {this.errorMessage}
//                                 </div>

//                                 <form
//                                     action="/"
//                                     onSubmit={this.submit}
//                                     class="grid grid--y-default"
//                                 >
//                                     <div class="col">
//                                         <Input
//                                             placeholder={'Ваше имя'}
//                                             vModel={this.formModel.name}
//                                             error={this.fieldErrors.name}
//                                             onInput={() => {
//                                                 this.deleteError('name');
//                                             }}
//                                         />
//                                     </div>
//                                     <div class="col">
//                                         <Input
//                                             type="tel"
//                                             mask={{
//                                                 mask: '+7 (999) 999-99-99[9]',
//                                                 placeholder: '_',
//                                                 showMaskOnHover: false,
//                                             }}
//                                             placeholder={'Номер телефона'}
//                                             vModel={this.formModel.phone}
//                                             error={this.fieldErrors.phone}
//                                             onInput={() => {
//                                                 this.deleteError('phone');
//                                             }}
//                                             inputmode="numeric"
//                                         />
//                                     </div>
//                                     {this.modalData.message && (
//                                         <div class="col">
//                                             <Input
//                                                 rows={2}
//                                                 vModel={this.formModel.message}
//                                                 placeholder="Сообщение"
//                                             />
//                                         </div>
//                                     )}
//                                     <div class="col text-left">
//                                         <Checkbox vModel={this.formModel.agreement}>
//                                             согласен с условиями передачи данных
//                                         </Checkbox>
//                                     </div>

//                                     <div class="feedback-error feedback-container__error-after">
//                                         {[].concat(this.formErrors).join(', ')}
//                                     </div>

//                                     <div class="col">
//                                         <Button
//                                             modifiers={['primary', 'block']}
//                                             type="submit"
//                                             loading={this.$wait.waiting('formSend')}
//                                             disabled={!this.formModel.agreement}
//                                         >
//                                             <span
//                                                 domPropsInnerHTML={
//                                                     this.modalData.submitButtonText
//                                                 }
//                                             />
//                                         </Button>
//                                     </div>
//                                 </form>
//                             </div>
//                         </div>
//                     </Modal>
//                 );
//             },
//         });
//     }
// }
